'use client';

import { logs } from '@opentelemetry/api-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs';

const BUILD_CONSOLE_DISABLED = process.env.NEXT_PUBLIC_LOGGING_DISABLED === '1';
const BUILD_POSTHOG_DISABLED = process.env.NEXT_PUBLIC_POSTHOG_LOGGING_DISABLED === '1';
interface LoggingWindow extends Window {
  __loggingDisabled?: boolean;
  __posthogLoggingDisabled?: boolean;
}

export type LogLevel = 'info' | 'warn' | 'error';

export type LogOptions = {
  /** Send this log payload to PostHog Logs product. */
  shipToPostHog?: boolean;
};

type OTelInitOptions = {
  apiKey: string;
  apiHost?: string;
  serviceName?: string;
  environment?: string;
  accountId?: number;
  accountName?: string;
};

type PendingShipLog = {
  level: LogLevel;
  channel: string;
  event: string;
  detail: Record<string, unknown>;
};

const MAX_PENDING_POSTHOG_LOGS = 500;
let otelProvider: LoggerProvider | null = null;
let otelLogger: ReturnType<typeof logs.getLogger> | null = null;
let otelInitFingerprint = '';
const pendingPostHogLogs: PendingShipLog[] = [];
const browserConsole = globalThis?.['console'];

/**
 * Channel → accent colour. Unregistered channels fall through to gray.
 */
export const CHANNEL_COLORS: Record<string, string> = {
  // Shared diagnostics
  'global-client': '#d2a8ff',

  // Section pins (desktop)
  'every-channel-pin': '#9febd7',
  'hero-pin': '#6ecc8b',
  'pricing-pin': '#399587',
  'product-screens-pin': '#4fafa0',
  'social-proof-pin': '#ffbb8a',
  'value-props-pin': '#e0a733',
  'work-pin': '#f57e56',

  // Section pins (mobile)
  'mobile-every-channel-pin': '#7ed9c6',
  'mobile-hero-pin': '#f0eee6',
  'mobile-pricing-pin': '#80d4ff',
  'mobile-product-screens-pin': '#3a9085',
  'mobile-social-proof-pin': '#ffd580',
  'mobile-value-props-pin': '#4fafa0',
};

function flattenAttributes(input: Record<string, unknown>, prefix = ''): Record<string, string | number | boolean> {
  const output: Record<string, string | number | boolean> = {};

  for (const [key, value] of Object.entries(input)) {
    const nextKey = prefix ? `${prefix}.${key}` : key;
    if (value === null || value === undefined) continue;

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      output[nextKey] = value;
      continue;
    }

    if (Array.isArray(value)) {
      output[nextKey] = JSON.stringify(value);
      continue;
    }

    if (typeof value === 'object') {
      Object.assign(output, flattenAttributes(value as Record<string, unknown>, nextKey));
      continue;
    }

    output[nextKey] = String(value);
  }

  return output;
}

function stripTrailingSlash(host: string): string {
  return host.replace(/\/+$/, '');
}

function enqueuePendingPostHogLog(entry: PendingShipLog): void {
  if (pendingPostHogLogs.length >= MAX_PENDING_POSTHOG_LOGS) {
    pendingPostHogLogs.shift();
  }
  pendingPostHogLogs.push(entry);
}

function flushPendingPostHogLogs(): void {
  if (!otelLogger || pendingPostHogLogs.length === 0) return;

  while (pendingPostHogLogs.length > 0) {
    const entry = pendingPostHogLogs.shift();
    if (!entry) break;
    emitToPostHog(entry.level, entry.channel, entry.event, entry.detail);
  }
}

export function initializePostHogLogging(options: OTelInitOptions): void {
  if (typeof window === 'undefined') return;
  if (!options.apiKey?.trim()) return;

  const apiHost = stripTrailingSlash(options.apiHost?.trim() || 'https://us.i.posthog.com');
  const serviceName = options.serviceName?.trim() || 'keystone-customer-site';
  const environment = options.environment?.trim() || 'production';
  const fingerprint = `${apiHost}|${options.apiKey}|${serviceName}|${environment}|${options.accountId ?? ''}|${options.accountName ?? ''}`;
  if (otelLogger && otelInitFingerprint === fingerprint) return;

  if (otelProvider) {
    // Cleanly replace logger provider when config changes.
    void otelProvider.shutdown().catch(() => {});
    otelProvider = null;
    otelLogger = null;
  }

  const baseAttributes: Record<string, string | number | boolean> = {
    'service.name': serviceName,
    environment,
    site_domain: window.location.hostname,
  };
  if (options.accountId !== undefined) baseAttributes.account_id = options.accountId;
  if (options.accountName) baseAttributes.account_name = options.accountName;

  const exporter = new OTLPLogExporter({
    url: `${apiHost}/i/v1/logs?token=${encodeURIComponent(options.apiKey)}`,
    headers: {
      // Keep request "simple" to avoid CORS preflight in browser.
      'Content-Type': 'text/plain',
    },
  });

  otelProvider = new LoggerProvider({
    resource: resourceFromAttributes(baseAttributes),
    processors: [new BatchLogRecordProcessor(exporter)],
  });

  logs.setGlobalLoggerProvider(otelProvider);
  otelLogger = logs.getLogger(serviceName);
  otelInitFingerprint = fingerprint;
  flushPendingPostHogLogs();
}

function isConsoleEnabled(): boolean {
  if (BUILD_CONSOLE_DISABLED) return false;
  if (typeof window === 'undefined') return true;
  return (window as LoggingWindow).__loggingDisabled !== true;
}

function isPostHogShippingEnabled(): boolean {
  if (BUILD_POSTHOG_DISABLED) return false;
  if (typeof window === 'undefined') return false;
  return (window as LoggingWindow).__posthogLoggingDisabled !== true;
}

function formatDetail(detail: Record<string, unknown>): string {
  return Object.entries(detail)
    .map(([k, v]) => `${k}=${typeof v === 'number' ? v.toFixed(4) : String(v)}`)
    .join('  ');
}

function emitConsole(
  level: LogLevel,
  channel: string,
  event: string,
  detail: Record<string, unknown>,
): void {
  const color = CHANNEL_COLORS[channel] ?? '#aaa';
  const detailStr = formatDetail(detail);
  const message = `%c[${channel}] %c${event}%c ${detailStr}`;
  const channelStyle = `color:${color}; font-weight:bold`;
  const eventStyle = level === 'error'
    ? 'color:#e94e4e; font-weight:bold'
    : level === 'warn'
      ? 'color:#f5a623; font-weight:bold'
      : 'color:#fff; font-weight:bold';
  const detailStyle = 'color:#999; font-weight:normal';

  if (level === 'error') {
    browserConsole?.error?.(message, channelStyle, eventStyle, detailStyle);
    return;
  }
  if (level === 'warn') {
    browserConsole?.warn?.(message, channelStyle, eventStyle, detailStyle);
    return;
  }
  browserConsole?.log?.(message, channelStyle, eventStyle, detailStyle);
}

function emitToPostHog(level: LogLevel, channel: string, event: string, detail: Record<string, unknown>): boolean {
  if (!otelLogger) return false;

  const severityText = level === 'warn' ? 'WARNING' : level === 'error' ? 'ERROR' : 'INFO';
  const attributes = flattenAttributes({ channel, ...detail });

  otelLogger.emit({
    severityText,
    body: event,
    attributes,
  });
  return true;
}

function emit(level: LogLevel, channel: string, event: string, detail: Record<string, unknown>, options?: LogOptions): void {
  const shouldShip = (options?.shipToPostHog ?? level !== 'info') && isPostHogShippingEnabled();
  // Keep warn/error visible even when regular logs are muted.
  if (level !== 'info' || isConsoleEnabled()) {
    emitConsole(level, channel, event, detail);
  }
  if (shouldShip) {
    const shipped = emitToPostHog(level, channel, event, detail);
    if (!shipped) {
      enqueuePendingPostHogLog({ level, channel, event, detail });
    }
  }
}

export function log(channel: string, event: string, detail: Record<string, unknown> = {}, options?: LogOptions): void {
  emit('info', channel, event, detail, options);
}

export function warn(channel: string, event: string, detail: Record<string, unknown> = {}, options?: LogOptions): void {
  emit('warn', channel, event, detail, options);
}

export function error(channel: string, event: string, detail: Record<string, unknown> = {}, options?: LogOptions): void {
  emit('error', channel, event, detail, options);
}
